import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import profile from '../assets/me.jpg';

const About = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: -25 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  const techStack = {
    frontend: ['React', 'Angular', 'JavaScript', 'HTML/CSS'],
    backend: ['Java', 'Python', 'Node.js', 'C/C++', 'Flask'],
    tools: ['Git', 'Docker', 'AWS', 'MongoDB', 'PostgreSQL']
  };

  return (
    <section className="about" id="about" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.span className="section-tag" variants={itemVariants}>
            <span className="tag-bracket">{'<'}</span>
            about
            <span className="tag-bracket">{'/>'}</span>
          </motion.span>
          <motion.h2 className="section-title" variants={itemVariants}>
            <span className="keyword">class</span> Developer{' '}
            <span className="bracket">{'{'}</span>
          </motion.h2>
        </motion.div>

        <div className="about-content">
          <motion.div
            className="about-image-wrapper"
            variants={imageVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="image-container">
              <motion.div 
                className="image-overlay"
                animate={{
                  x: isHovered ? (mousePosition.x - 200) * 0.02 : 0,
                  y: isHovered ? (mousePosition.y - 200) * 0.02 : 0
                }}
              />
              <img
                src={profile}
                alt="Divita Phadakale"
                className="profile-image"
              />
              <motion.div 
                className="image-frame"
                animate={{
                  x: isHovered ? (mousePosition.x - 200) * 0.05 : 0,
                  y: isHovered ? (mousePosition.y - 200) * 0.05 : 0
                }}
              />
              <div className="image-label">
                <span className="label-text">DEVELOPER</span>
                <div className="label-line"></div>
              </div>
            </div>

            <motion.div 
              className="profile-card"
              whileHover={{ scale: 1.05, rotateX: 5 }}
            >
              <div className="card-header">
                <div className="card-icon"></div>
                <div className="card-status">
                  <span className="status-indicator"></span>
                  Active
                </div>
              </div>
              <h3>Divita Phadakale</h3>
              <p className="card-role">Software Developer</p>
              <div className="card-stats">
                <div className="card-stat">
                  {/* <span className="stat-icon"></span> */}
                  <span>CU Boulder</span>
                </div>
                <div className="card-stat">
                  {/* <span className="stat-icon"></span> */}
                  <span>Boulder, CO</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="about-text"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <motion.div className="text-block" variants={itemVariants}>
              <h3 className="about-subtitle">
                <span className="comment">// Who am I?</span>
              </h3>
              <p className="about-description">
                My name is <span className="highlight">Divita Phadakale</span>. I'm a{' '}
                <span className="highlight">Software Developer</span> and graduate student at{' '}
                <span className="highlight">CU Boulder</span>, specializing in Software Systems and Cloud Computing. I don't just build applications; I architect <span className="tech-highlight">scalable</span>, 
                <span className="tech-highlight"> secure</span>, and <span className="tech-highlight">intelligent</span> digital 
                ecosystems that solve real-world problems.
              </p>
              <p className="about-description">
                With over <span className="highlight">2+ years</span> of industry experience at TIAA and 
                a track record of <span className="highlight">15+ projects</span>, I bring a unique blend of 
                practical engineering expertise and cutting-edge research knowledge to every challenge.
              </p>
            </motion.div>

            <motion.div className="tech-stack-wrapper" variants={itemVariants}>
              <h4 className="stack-title">
                <span className="comment">// My Tech Arsenal</span>
              </h4>
              
              <div className="tech-categories">
                <motion.div 
                  className="tech-category"
                  whileHover={{ x: 10 }}
                >
                  <h5 className="category-name">
                    <span className="keyword">List <span className="tag-bracket">{'<'}</span>String</span><span className="tag-bracket">{'>'}</span> frontend = <span className="bracket">[</span>
                  </h5>
                  <div className="tech-list">
                    {techStack.frontend.map((tech, index) => (
                      <motion.span
                        key={tech}
                        className="tech-item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        whileHover={{ scale: 1.1, color: 'var(--accent-green)' }}
                      >
                        '{tech}'{index < techStack.frontend.length - 1 ? ',' : ''}
                      </motion.span>
                    ))}
                  </div>
                  <span className="bracket">]</span>
                </motion.div>

                <motion.div 
                  className="tech-category"
                  whileHover={{ x: 10 }}
                >
                  <h5 className="category-name">
                    <span className="keyword">List <span className="tag-bracket">{'<'}</span>String</span><span className="tag-bracket">{'>'}</span> backend = <span className="bracket">[</span>
                  </h5>
                  <div className="tech-list">
                    {techStack.backend.map((tech, index) => (
                      <motion.span
                        key={tech}
                        className="tech-item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.7 + index * 0.1 }}
                        whileHover={{ scale: 1.1, color: 'var(--accent-cyan)' }}
                      >
                        '{tech}'{index < techStack.backend.length - 1 ? ',' : ''}
                      </motion.span>
                    ))}
                  </div>
                  <span className="bracket">]</span>
                </motion.div>

                <motion.div 
                  className="tech-category"
                  whileHover={{ x: 10 }}
                >
                  <h5 className="category-name">
                    <span className="keyword">List <span className="tag-bracket">{'<'}</span>String</span><span className="tag-bracket">{'>'}</span> tools = <span className="bracket">[</span>
                  </h5>
                  <div className="tech-list">
                    {techStack.tools.map((tech, index) => (
                      <motion.span
                        key={tech}
                        className="tech-item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.9 + index * 0.1 }}
                        whileHover={{ scale: 1.1, color: 'var(--accent-purple)' }}
                      >
                        '{tech}'{index < techStack.tools.length - 1 ? ',' : ''}
                      </motion.span>
                    ))}
                  </div>
                  <span className="bracket">]</span>
                </motion.div>
              </div>
            </motion.div>

            <motion.div className="about-actions" variants={itemVariants}>
              <motion.a 
                href="https://drive.google.com/file/d/147RQcVESeFpmSnhCrQTAAAr97fdf2JO7/view?usp=sharing" 
                className="btn-download"
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px var(--glow-green)" }}
                whileTap={{ scale: 0.95 }}
                download
              >
          
                Download CV
              </motion.a>
              <motion.a 
                href="#experience" 
                className="btn-link"
                whileHover={{ x: 5 }}
              >
                View Experience →
              </motion.a>
            </motion.div>
          </motion.div>
        </div>

        <motion.div 
          className="section-footer"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1.2 }}
        >
          <span className="bracket">{'}'}</span>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
